import { COLORS, STATUS } from '@btime/constants'
import { path } from 'ramda'
import { useSessionPermission } from '@btime/react-session'
import { useTranslation } from 'react-i18next'
import React from 'react'

import { Col, Grid } from '../../../../packages/kiti/components/structure/Grid'
import { useForm, useFormState } from '../../../../packages/kiti/components/form/form/FormHandler'
import { useGetFieldValueAddressFromApi } from '../../../../utils/hooks/fieldValueAddress'
import AddressInput from '../../../../components/form/AddressInput'
import AssetSelect from '../../../../containers/assets/select/AssetSelect'
import ChecklistSelect from '../../../../containers/checklists/select/ChecklistSelect'
import DateInput from '../../../../components/form/date/DateInput'
import FormItem from '../../../../packages/kiti/components/form/form/FormItem'
import LoadingWrapper from '../../../../packages/kiti/components/helpers/LoadingWrapper'
import PlaceSelect from '../../../../containers/places/select/PlaceSelect'
import PrioritySelect from '../../../../containers/serviceOrders/select/PrioritySelect'
import TagSelect from '../../../../containers/tags/select/TagSelect'
import { ACTIVITY_TAG_TYPE } from '../../../tags/_constants'
import UsersOrGroupsSelect from '../../../../containers/users/select/UsersOrGroupsSelect'
import Modal from '../../../../components/structure/Modal'
import LocksContent from '../../../places/show/tabs/locks/Locks'
import Separator from '../../../../packages/kiti/components/helpers/Separator'
import Text, { Title } from '../../../../packages/kiti/components/presentation/Text'
import Line from '../../../../packages/kiti/components/helpers/Line'
import { usePlaceTypesWithPermission } from '../../../../utils/helpers/placeTypes'
import Select from '../../../../packages/kiti/components/form/Select'

const GeneralSection = () => {
  const { t } = useTranslation('editServiceOrder')
  const form = useForm()
  const isEditingExistingServiceOrder = !!form?.id
  const hasAddLeanServiceOrderPermission =
    useSessionPermission('createLeanServiceOrder') && !isEditingExistingServiceOrder
  const canUseName = useSessionPermission('editServiceOrdersName') && !hasAddLeanServiceOrderPermission
  const canEditUser = useSessionPermission('editFinishedServiceOrdersUser')
  const canSeeOnlySelfSOs = useSessionPermission('seeOnlySelfServiceOrders')
  const hidePlace = useSessionPermission('hideServiceOrdersPlace')
  const canUseAutomaticDistribution = useSessionPermission('useAutomaticDistribution')
  const canSelectAsset = useSessionPermission('listAsset') && !hidePlace
  const enabledPlaceLock = useSessionPermission('enablePlaceLock')
  const [open, setOpen] = React.useState()
  const [selectedPlace, setSelectedPlace] = React.useState()
  const { handleChange, handleChangeIn } = useFormState()
  const canEditAll = !form?.id || [STATUS.OPEN, STATUS.REQUEST].includes(path(['status', 'name'], form))
  const [getPlaceAddress, { loading }] = useGetFieldValueAddressFromApi()
  const placeTypes = usePlaceTypesWithPermission('list')

  const afterChangePlace = place => {
    getPlaceAddress(place?.id, place?.type).then(handleChange('address'))
    handleChange('asset', undefined)
  }

  const handleAfterChangeChecklist = () => {
    if (enabledPlaceLock) handleChangeIn(['place'], null)
  }

  const handleAfterChangePlaceType = () => {
    handleChange('place', undefined)
  }

  const reasonForDisablePresenter = place => {
    setOpen(true)
    setSelectedPlace(place)
  }

  return (
    <>
      <Grid paddingGutter={0.5} wrap>
        {canUseName && (
          <Col width="50%" wrapOn="sm">
            <FormItem label={t('name')} field="name" />
          </Col>
        )}

        <Col width="50%" wrapOn="sm">
          <FormItem
            label={t('checklist')}
            field="checklist"
            Input={ChecklistSelect}
            disabled={!canEditAll}
            filter={{ usages: ['serviceOrder'], includeDisabled: false }}
            afterChange={handleAfterChangeChecklist}
          />
        </Col>

        {!hidePlace && (
          <>
            <Col width="50%" wrapOn="sm">
              <FormItem
                label={t('placeType')}
                field="placeType"
                Input={Select}
                afterChange={handleAfterChangePlaceType}
                options={placeTypes.map(type => ({ id: type.id, value: type.key, name: type.namePlural }))}
              />
            </Col>
            <Col width="50%" wrapOn="sm">
              <FormItem
                label={t('place')}
                field="place"
                Input={PlaceSelect}
                afterChange={afterChangePlace}
                reasonForDisablePresenter={reasonForDisablePresenter}
                type={form?.placeType?.value}
                filter={{
                  lock: { checklistId: form.checklist?.id }
                }}
                ignoreDisable={false}
              />
            </Col>
          </>
        )}

        {canSelectAsset && (
          <Col width="100%" wrapOn="sm">
            <FormItem
              label={t('asset')}
              field="asset"
              Input={AssetSelect}
              filter={{ placeIds: form.place?.id }}
              disabled={!form.place?.id}
            />
          </Col>
        )}

        {!canSeeOnlySelfSOs && (
          <Col width="100%">
            <FormItem
              label={t('responsible')}
              field="userOrGroup"
              Input={UsersOrGroupsSelect}
              disabled={!canEditUser && !canEditAll}
              address={form.address}
              showAutomaticOption={canUseAutomaticDistribution}
            />
          </Col>
        )}

        {!hasAddLeanServiceOrderPermission && (
          <>
            <Col width="100%">
              <LoadingWrapper loading={loading}>
                <FormItem label={t('address')} field="address" Input={AddressInput} />
              </LoadingWrapper>
            </Col>

            <Col width="50%" wrapOn="sm">
              <FormItem label={t('scheduling')} field="scheduling" Input={DateInput} useTime />
            </Col>

            <Col width="50%" wrapOn="sm">
              <FormItem label={t('priority')} field="priority" Input={PrioritySelect} />
            </Col>

            <Col width="50%" wrapOn="sm">
              <FormItem
                label={t('tag')}
                field="tag"
                Input={TagSelect}
                type={ACTIVITY_TAG_TYPE}
                allowCreate={false}
              />
            </Col>
          </>
        )}
      </Grid>
      <Modal open={open && selectedPlace} onClose={() => setOpen(false)} width="46rem">
        <Title size="h3" strong color={COLORS.dark2} noMargin>
          {t('lockReasons')}
        </Title>
        {selectedPlace && <Text>{selectedPlace.name}</Text>}
        <Separator weight={2} />
        <Line />
        <Separator weight={2} />
        <LocksContent checklistId={form.checklist?.id} placeId={selectedPlace?.id} />
      </Modal>
    </>
  )
}

export default GeneralSection
